<template>
  <RevTable :collection="tableData" :columns="tableColumns" transparent-header>
    <template #body-all="{ item }">
      <div class="md:flex md:w-full md:justify-center">
        <RevToggle
          :model-value="item.all"
          @update:model-value="
            handleToggleAllEmailTypesForContact(item.id, $event)
          "
        />
      </div>
    </template>

    <template
      v-for="emailType in props.emailTypes"
      :key="emailType.value"
      #[`body-${emailType.value}`]="{ item }"
    >
      <div class="md:flex md:w-full md:justify-center">
        <RevToggle
          :model-value="
            contactEmailTypeStates[item.id]?.[emailType.value] ?? false
          "
          @update:model-value="
            handleToggleEmailType(item.id, emailType.value, $event)
          "
        />
      </div>
    </template>

    <template #body-action="{ item }">
      <div class="flex w-full justify-end">
        <RevButtonIcon
          :icon="IconTrash"
          variant="secondary"
          @click="handleOpenDeleteModal(item.id)"
        />

        <RevDialog
          :name="`${deleteModalName}-${item.id}`"
          :title="i18n(translations.deleteModalTitle)"
        >
          <template #body>
            <div class="mb-32 text-center">
              {{ i18n(translations.deleteModalPrompt) }}
            </div>

            <RevButton
              class="mb-8"
              full-width="always"
              :loading="isDeleting"
              variant="primary"
              @click="handleDelete"
            >
              {{ i18n(translations.deleteModalConfirm) }}
            </RevButton>

            <RevButton
              full-width="always"
              variant="secondary"
              @click="closeModal(`${deleteModalName}-${item.id}`)"
            >
              {{ i18n(translations.deleteModalCancel) }}
            </RevButton>
          </template>
        </RevDialog>
      </div>
    </template>
  </RevTable>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useDebounceFn } from '@backmarket/utils/composables/useDebouncedFn'
import { RevButton } from '@ds/components/Button'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { RevDialog } from '@ds/components/Dialog'
import { closeModal, openModal } from '@ds/components/ModalBase'
import { RevTable } from '@ds/components/Table'
import { RevToggle } from '@ds/components/Toggle'
import { IconTrash } from '@ds/icons/IconTrash'

import translations from '../Mailbook.translations'

const emit = defineEmits<{
  (e: 'contact-deleted'): void
}>()

const props = defineProps<{
  tableName: string
  emailTypes: {
    value: string
    label: string
  }[]
  contacts: {
    id: string
    emailAddress: string
    emailTypes: {
      value: string
      label: string
      enabled: boolean
    }[]
  }[]
}>()

const $httpFetch = useHttpFetch()
const { openErrorToast, openSuccessToast } = useTheToast()

const i18n = useI18n()
const isDeleting = ref(false)
const contactToDelete = ref<string | null>(null)
const contactEmailTypeStates = ref<Record<string, Record<string, boolean>>>({})

const initializeContactEmailTypeStates = () => {
  const states: Record<string, Record<string, boolean>> = {}

  props.contacts.forEach((contact) => {
    states[contact.id] = {}

    contact.emailTypes.forEach((emailType) => {
      states[contact.id][emailType.value] = emailType.enabled
    })
  })

  contactEmailTypeStates.value = states
}

watch(() => props.contacts, initializeContactEmailTypeStates, {
  immediate: true,
})

const deleteModalName = computed(() => `delete-contact-${props.tableName}`)
const tableColumns = computed(() => [
  {
    key: 'email',
    label: i18n(translations.email),
  },
  ...(props.emailTypes.length > 0
    ? [
        {
          key: 'all',
          label: i18n(translations.allEmails),
          headerClassNames: 'md:flex md:w-full md:justify-center',
        },
      ]
    : []),
  ...props.emailTypes.map((emailType) => ({
    key: emailType.value,
    label: emailType.label,
    headerClassNames: 'md:flex md:w-full md:justify-center',
  })),
  {
    key: 'action',
    label: '',
    headerClassNames: 'md:flex md:w-full md:justify-center',
  },
])

const tableData = computed(() => {
  return props.contacts
    .map((contact) => {
      return {
        id: contact.id,
        email: contact.emailAddress,
        all: props.emailTypes.every(
          (emailType) =>
            contactEmailTypeStates.value[contact.id]?.[emailType.value] ===
            true,
        ),
        ...Object.fromEntries(
          props.emailTypes.map((emailType) => [
            emailType.value,
            contactEmailTypeStates.value[contact.id]?.[emailType.value] ??
              false,
          ]),
        ),
        action: '',
      }
    })
    .sort((a, b) => a.email.localeCompare(b.email))
})

async function updateContactEmailTypeAccess(
  id: string,
  item: Record<string, unknown>,
) {
  await $httpFetch('/api/seller-experience/mailbook/:contactId/types', {
    method: 'PUT',
    pathParams: { contactId: id },
    body: {
      emailTypes: props.emailTypes.map((emailType) => ({
        code: emailType.value,
        enabled: item[emailType.value] as boolean,
      })),
    },
  })
}

function toggleAllEmailTypesForContact(contactId: string, newValue: boolean) {
  if (!contactEmailTypeStates.value[contactId]) {
    contactEmailTypeStates.value[contactId] = {}
  }
  props.emailTypes.forEach((emailType) => {
    contactEmailTypeStates.value[contactId][emailType.value] = newValue
  })

  const contactData = tableData.value.find(
    (contact) => contact.id === contactId,
  )

  if (contactData) {
    updateContactEmailTypeAccess(contactId, contactData)
  }
}

const handleToggleAllEmailTypesForContact = useDebounceFn(
  toggleAllEmailTypesForContact,
  300,
)

function toggleEmailType(
  contactId: string,
  emailTypeValue: string,
  value: boolean,
) {
  if (!contactEmailTypeStates.value[contactId]) {
    contactEmailTypeStates.value[contactId] = {}
  }
  contactEmailTypeStates.value[contactId][emailTypeValue] = value

  const contactData = tableData.value.find(
    (contact) => contact.id === contactId,
  )

  if (contactData) {
    updateContactEmailTypeAccess(contactId, contactData)
  }
}

const handleToggleEmailType = useDebounceFn(toggleEmailType, 300)

function handleOpenDeleteModal(contactId: string) {
  openModal(`${deleteModalName.value}-${contactId}`)
  contactToDelete.value = contactId
}

async function handleDelete() {
  try {
    if (!contactToDelete.value) {
      return
    }

    isDeleting.value = true

    await $httpFetch('/api/seller-experience/mailbook/:contactId', {
      method: 'DELETE',
      pathParams: { contactId: contactToDelete.value },
    })

    emit('contact-deleted')
    openSuccessToast({ title: i18n(translations.toastDeleteSuccessContent) })
  } catch (error) {
    openErrorToast({ title: i18n(translations.toastDeleteErrorContent) })
  } finally {
    isDeleting.value = false
  }
}
</script>
